const read = (value, camelName, pascalName) => (
  value?.[camelName] ?? value?.[pascalName]
);

const text = (value, camelName, pascalName) => String(
  read(value, camelName, pascalName) || '',
).trim();

const positiveInteger = value => {
  const source = value == null ? '' : String(value).trim();
  if (!/^\d+$/.test(source)) return null;
  const parsed = Number(source);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

const findById = (values, id) => (
  id
    ? (values || []).find(value => text(value, 'id', 'Id') === id) || null
    : null
);

export const ADMIN_CONTENT_FILTER_KEYS = [
  'regionId',
  'minLevel',
  'maxLevel',
];

export function normalizeAdminContentFilter(value = {}) {
  let minLevel = positiveInteger(value.minLevel);
  let maxLevel = positiveInteger(value.maxLevel);
  if (minLevel && maxLevel && minLevel > maxLevel) {
    [minLevel, maxLevel] = [maxLevel, minLevel];
  }
  const regionId = String(value.regionId || '').trim();
  return {
    regionId,
    minLevel,
    maxLevel,
    active: Boolean(regionId || minLevel || maxLevel),
  };
}

export function parseAdminContentFilter(search = '') {
  const params = new URLSearchParams(search);
  return normalizeAdminContentFilter({
    regionId: params.get('regionId'),
    minLevel: params.get('minLevel'),
    maxLevel: params.get('maxLevel'),
  });
}

function setFilterParams(params, filter) {
  ADMIN_CONTENT_FILTER_KEYS.forEach(key => params.delete(key));
  if (filter.regionId) params.set('regionId', filter.regionId);
  if (filter.minLevel) params.set('minLevel', String(filter.minLevel));
  if (filter.maxLevel) params.set('maxLevel', String(filter.maxLevel));
  return params;
}

export function updateAdminContentFilterSearch(search, patch) {
  const filter = normalizeAdminContentFilter({
    ...parseAdminContentFilter(search),
    ...patch,
  });
  const params = setFilterParams(new URLSearchParams(search), filter);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function adminContentFilterSearch(filter) {
  const params = setFilterParams(
    new URLSearchParams(),
    normalizeAdminContentFilter(filter),
  );
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function clearAdminContentFilterSearch(search) {
  const params = new URLSearchParams(search);
  ADMIN_CONTENT_FILTER_KEYS.forEach(key => params.delete(key));
  const query = params.toString();
  return query ? `?${query}` : '';
}

function regionMetadata(region) {
  const level = positiveInteger(read(region, 'level', 'Level'));
  return {
    regionId: text(region, 'id', 'Id') || null,
    levelRange: level ? [level, level] : null,
  };
}

function enemyMetadata(enemy, context) {
  const regionId = text(enemy, 'regionId', 'RegionId');
  const region = findById(context.regions, regionId);
  const override = positiveInteger(read(enemy, 'levelOverride', 'LevelOverride'));
  const regionLevel = positiveInteger(read(region, 'level', 'Level'));
  const level = override || regionLevel;
  return {
    regionId: region ? regionId : null,
    levelRange: level ? [level, level] : null,
  };
}

function recipeLevelRange(value) {
  const craftedLevel = positiveInteger(read(
    value,
    'craftedItemLevel',
    'CraftedItemLevel',
  ));
  if (craftedLevel) return [craftedLevel, craftedLevel];
  const tier = positiveInteger(read(value, 'recipeTier', 'RecipeTier'));
  return tier ? [((tier - 1) * 5) + 1, tier * 5] : null;
}

function itemMetadata(item, context) {
  const explicitRegionId = text(item, 'regionId', 'RegionId');
  const enemyId = text(item, 'enemyId', 'EnemyId');
  const enemy = findById(context.enemies, enemyId);
  const enemyValues = enemy ? enemyMetadata(enemy, context) : null;
  const region = findById(context.regions, explicitRegionId);
  const regionLevel = positiveInteger(read(region, 'level', 'Level'));
  const craftable = Boolean(read(item, 'isCraftable', 'IsCraftable'));
  return {
    regionId: region
      ? explicitRegionId
      : explicitRegionId
        ? null
        : enemyValues?.regionId || null,
    levelRange: enemyValues?.levelRange
      || (regionLevel ? [regionLevel, regionLevel] : null)
      || (craftable ? recipeLevelRange(item) : null),
  };
}

function recipeMetadata(recipe, context) {
  const outputId = text(recipe, 'outputItemId', 'OutputItemId');
  const output = outputId
    ? findById(context.items, outputId)
    : recipe;
  return {
    regionId: output ? itemMetadata(output, context).regionId : null,
    levelRange: recipeLevelRange(recipe)
      || (output ? recipeLevelRange(output) : null),
  };
}

function levelMetadata(level, context, filter) {
  const levelNumber = positiveInteger(read(level, 'levelNumber', 'LevelNumber'));
  const selectedRegion = findById(context.regions, filter.regionId);
  const selectedRegionLevel = positiveInteger(read(
    selectedRegion,
    'level',
    'Level',
  ));
  return {
    regionId: selectedRegionLevel
      ? selectedRegionLevel === levelNumber
        ? filter.regionId
        : '__outside-selected-region-level__'
      : null,
    levelRange: levelNumber ? [levelNumber, levelNumber] : null,
  };
}

export function adminContentMetadata(kind, value, context = {}, filter = {}) {
  if (kind === 'region') return regionMetadata(value);
  if (kind === 'enemy') return enemyMetadata(value, context);
  if (kind === 'item') return itemMetadata(value, context);
  if (kind === 'recipe') return recipeMetadata(value, context);
  if (kind === 'level') return levelMetadata(value, context, filter);
  return { regionId: null, levelRange: null };
}

export function classifyAdminContent(kind, value, filterValue, context = {}) {
  const filter = normalizeAdminContentFilter(filterValue);
  if (!filter.active) return 'match';
  const metadata = adminContentMetadata(kind, value, context, filter);
  const results = [];

  if (filter.regionId) {
    results.push(metadata.regionId == null
      ? null
      : metadata.regionId === filter.regionId);
  }
  if (filter.minLevel || filter.maxLevel) {
    if (!metadata.levelRange) {
      results.push(null);
    } else {
      const [minimum, maximum] = metadata.levelRange;
      results.push(
        (!filter.minLevel || maximum >= filter.minLevel)
        && (!filter.maxLevel || minimum <= filter.maxLevel),
      );
    }
  }

  if (results.includes(false)) return 'outside';
  if (results.includes(null)) return 'uncategorized';
  return 'match';
}

export function partitionAdminContent(
  values,
  kind,
  filter,
  context = {},
) {
  return (values || []).reduce((groups, value) => {
    groups[classifyAdminContent(kind, value, filter, context)].push(value);
    return groups;
  }, { match: [], uncategorized: [], outside: [] });
}
